/**
 * The standings.
 *
 * A table, not a chart. Eight-plus miners each carrying a name, a count, a
 * share, a hashrate and a streak is exactly the case the form heuristic sends
 * to a table — and a miner looking for their own row wants to *read* it, not
 * hunt for a colour in a legend.
 *
 * The one graphical element is the share bar: single hue, magnitude only. A
 * pinned miner's row is marked three ways at once — a left rule, a wash, and an
 * explicit "YOU" chip — so identity never rests on colour alone.
 */

import { Link } from 'react-router-dom'

import type { LeaderboardRow } from '../api/generated/LeaderboardRow'
import { ago, hashrate, share, tooFewBlocks, uncertainty } from '../lib/format'
import { href } from '../lib/routes'
import { usePinnedMiners } from '../lib/store'

function Row({
  row,
  chain,
  max,
  mine,
  onToggle,
}: {
  row: LeaderboardRow
  chain: string | null
  max: number
  mine: boolean
  onToggle: (miner: string) => void
}) {
  const named = row.attribution === 'telemetry'
  // A name this operator earned on another chain, under the same reward
  // preimage. Shown because a preimage tells a miner nothing about themselves,
  // but never dressed as a live observation: nothing here says the node is on
  // this chain, only that this is who the operator was last known to be.
  const carried = row.attribution === 'carried'
  // Thin evidence: a single supporting block, or a lead the rest of the votes
  // do not back. Either way the mapping is a guess that a later block may undo.
  const tentative = named && (row.attribution_votes < 3 || row.confidence < 0.6)
  return (
    <tr className={mine ? 'mine' : undefined}>
      <td className={`rank rank-${row.rank}`}>{row.rank}</td>
      <td className="left">
        <div className="miner-cell">
          <button
            className="pin-button"
            aria-pressed={mine}
            title={mine ? 'Remove from your miners' : 'Mark this miner as yours'}
            onClick={() => onToggle(row.miner)}
          >
            {mine ? '★' : '☆'}
          </button>
          <Link
            to={href({ chain, miner: row.miner })}
            className={named || carried ? 'miner-name' : 'miner-name anonymous'}
            // The address first: it is the identifier the miner recognises. The
            // preimage follows because it is what the header actually carries
            // and the only one the chain asserts.
            title={row.address ? `${row.address}\n${row.miner}` : row.miner}
          >
            {/* `display` already carries the abbreviated reward address when
                no name is known — the backend derives it, because the preimage
                is exact and unrecognisable. */}
            {row.display}
          </Link>
          {mine && <span className="chip chip-you">You</span>}
          {carried && (
            <span
              className="chip chip-named chip-guess"
              title={
                'Name carried from another chain, matched on the same reward preimage — ' +
                'this operator has not yet been resolved by telemetry here.'
              }
            >
              elsewhere
            </span>
          )}
          {named && (
            <span
              // An attributed name is an inference from who reported the block
              // first, not a claim the miner made. A name is offered as soon as
              // one node leads, so the chip has to distinguish a thin guess from
              // a settled mapping — `100% of 1` and `86% of 7` are not the same
              // claim, and a percentage alone cannot tell them apart.
              className={tentative ? 'chip chip-named chip-guess' : 'chip chip-named'}
              title={
                row.attribution_votes === 0
                  ? // Held from evidence that has since aged out of the vote
                    // window. "0% of 0" would be arithmetic nonsense; the
                    // mapping is not less true than it was, only less recently
                    // corroborated, and the row should say so.
                    'Name inferred from telemetry, on evidence older than the current window — ' +
                    'no recent block resolved a first reporter. Best effort; a later block can correct it.'
                  : `Name inferred from telemetry: ${Math.round(row.confidence * 100)}% of ` +
                    `${row.attribution_votes} attributed ` +
                    (row.attribution_votes === 1 ? 'block points' : 'blocks point') +
                    ` here. Best effort — a later block can correct it.`
              }
            >
              {tentative ? 'node?' : 'node'}
            </span>
          )}
        </div>
      </td>
      <td className="numeral">{row.blocks}</td>
      {/* Bar and number in one cell: two columns for one quantity cost ~80px
          and pushed the streak and last-block columns off the table at ordinary
          window widths. The bar is the magnitude, the number is its direct
          label — they belong together. */}
      <td className="share-cell">
        <span className="numeral">{share(row.share)}</span>
        <div className="share-bar" aria-hidden="true">
          <i style={{ width: `${max > 0 ? (row.share / max) * 100 : 0}%` }} />
        </div>
      </td>
      <td className="numeral">
        <span className="rate-cell">
          <span className={tooFewBlocks(row.hashrate_relative_error) ? 'rate-weak' : undefined}>
            {hashrate(row.hashrate_estimate)}
          </span>
          {/* The sample behind the figure. A row resting on one or two blocks
              is a coin toss dressed as a measurement, and saying so costs one
              small line. */}
          {uncertainty(row.hashrate_relative_error) && (
            <span
              className="rate-error"
              title={`Estimated from ${row.blocks} block${row.blocks === 1 ? '' : 's'} in this window. Block finding is Poisson, so the relative error is 1/√blocks — it takes 25 blocks to reach ±20%.`}
            >
              {uncertainty(row.hashrate_relative_error)}
            </span>
          )}
        </span>
      </td>
      <td className="numeral streak" title="Longest run of consecutive blocks in this window">
        {row.best_streak > 1 ? `×${row.best_streak}` : '—'}
      </td>
      <td className="numeral last-seen" style={{ color: 'var(--text-muted)' }}>
        {ago(row.last_seen)}
      </td>
    </tr>
  )
}

export function Leaderboard({
  rows,
  chain,
  ready,
}: {
  rows: LeaderboardRow[]
  /** Needed to address a miner; null before the first chain has resolved. */
  chain: string | null
  ready: boolean
}) {
  const { isPinned, toggle } = usePinnedMiners()
  // Bars are scaled to the leader, not to 100%: with twenty miners the top
  // share is often under 15%, and full-scale bars would all be slivers.
  const max = rows.length > 0 ? Math.max(...rows.map((r) => r.share)) : 0

  if (rows.length === 0) {
    return (
      <p className="empty">
        {ready
          ? 'No blocks observed in this window yet. A shorter window may show more.'
          : 'Waiting for the first snapshot…'}
      </p>
    )
  }

  return (
    <div className="scroll-x">
      <table className="board">
        <caption className="visually-hidden">
          Miners ranked by blocks won in the selected window
        </caption>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col" className="left">
              Miner
            </th>
            <th scope="col">Blocks</th>
            <th scope="col">Share</th>
            <th scope="col">Est. hashrate</th>
            <th scope="col" className="streak">
              Streak
            </th>
            <th scope="col" className="last-seen">
              Last block
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Row
              key={row.miner}
              row={row}
              max={max}
              mine={isPinned(row.miner)}
              chain={chain}
              onToggle={toggle}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
